import React, { useCallback, useMemo } from 'react';
import { hasFlag } from 'country-flag-icons';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import { SxProps, Theme } from '@mui/material';
import { CountryCodes } from '@cnx-saas/common';
import { CountryNameLanguages, countryNames } from '../constants';
import { getLanguage } from '../utils';
import { SelectFieldProps, SelectField } from './SelectField';

export function SelectCountry(
  props: SelectFieldProps<CountryCodes> & {
    countries: { code: string; name: string }[];
    style?: SxProps<Theme> | undefined;
  },
) {
  const countries =
    props.countries ?? countryNames(getLanguage() as CountryNameLanguages);

  const getCountryLabel = useCallback(
    (code: string) => {
      return countries.find((country) => country.code === code)?.name ?? code;
    },
    [countries],
  );

  const desiredCodes = countries.map((country) => country.code);
  const options = useMemo(
    () =>
      Object.values(CountryCodes)
        .filter(
          (countryCode) =>
            hasFlag(countryCode) && desiredCodes.includes(countryCode),
        )
        .sort((a, b) => getCountryLabel(a).localeCompare(getCountryLabel(b)))
        .map((countryCode) => {
          return {
            value: countryCode,
            label: (
              <div>
                <span>{getUnicodeFlagIcon(countryCode)}</span>{' '}
                {getCountryLabel(countryCode)}
              </div>
            ),
          };
        }),
    [desiredCodes, getCountryLabel],
  );

  // Temp fix until Polaris will be migrated to new version of Material-UI
  if (!options || options.length === 0) return null;

  return <SelectField<CountryCodes> {...props} options={options} />;
}
